/**
 * AgendaView — phone agenda for swimlane-backed schedules (Phase 14.6).
 *
 * The technician swimlane (`Calendar.SwimGrid`, 20 slots × 54px ≈ 1,080px)
 * cannot shrink below tablet width, so phones render this chronological
 * agenda instead: a horizontal day strip, a utilization card, and jobs
 * grouped by hour with a colored left stripe per event tone.
 *
 *   <AgendaView.DayStrip days={…} onSelect={…} />
 *   <AgendaView>
 *     <AgendaView.UtilCard title={…} pct={26} stats={…} />
 *     <AgendaView.Hour>07:00</AgendaView.Hour>
 *     <AgendaView.Job time="07:00" title="Kozel Radoš" tone="ok" now … />
 *   </AgendaView>
 *
 * Pages own the breakpoint switch (`useBreakpoint`) and the scroll container;
 * tablet + desktop keep the swimlane. Tone vocabulary matches `EventChip`.
 *
 * Source: .agents/design/responsive/responsive-app.jsx PhoneServis
 * (DayStrip, UtilCard, ServisAgenda — lines 762-926).
 */

import type { ReactNode } from 'react';
import type { EventChipStatus, EventChipTone } from './EventChip';
import { Icon } from './Icon';

const STRIPE_CLASSES: Record<EventChipTone, string> = {
  accent: 'border-l-accent',
  warn: 'border-l-warn',
  ok: 'border-l-ok',
  ink: 'border-l-ink',
  neutral: 'border-l-ink-3',
  danger: 'border-l-red',
};

interface AgendaViewProps {
  children: ReactNode;
  className?: string;
}

export function AgendaView({ children, className = '' }: AgendaViewProps) {
  // Horizontal padding is owned by the page's scroll container so the
  // utilization card and the job list share one gutter.
  return <div className={'pb-8 pt-2 ' + className}>{children}</div>;
}

/* ── Day strip ─────────────────────────────────────────────────────────── */

export interface AgendaDay {
  /** Stable identity passed back through `onSelect` (e.g. the ISO date). */
  key: string;
  /** Two-letter weekday label ("Po", "Út", …). */
  dow: string;
  /** Day-of-month number. */
  dayNum: string | number;
  active?: boolean;
  /** Dimmed (e.g. weekend). */
  muted?: boolean;
  /** Small dot marking a day that has events. */
  pip?: boolean;
}

interface DayStripProps {
  days: AgendaDay[];
  onSelect?: (key: string) => void;
  'aria-label'?: string;
}

function DayStrip({ days, onSelect, 'aria-label': ariaLabel }: DayStripProps) {
  return (
    <div
      aria-label={ariaLabel ?? 'Výběr dne'}
      className="flex shrink-0 overflow-x-auto border-b border-line bg-paper px-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {days.map((d) => (
        <button
          key={d.key}
          type="button"
          aria-label={`${d.dow} ${d.dayNum}.`}
          aria-current={d.active ? 'date' : undefined}
          onClick={onSelect ? () => onSelect(d.key) : undefined}
          className={
            '-mb-px min-w-[52px] flex-1 border-b-2 px-1 pb-2 pt-2.5 text-center ' +
            (d.active ? 'border-accent ' : 'border-transparent ')
          }
        >
          <span
            className={
              'block font-mono text-[9.5px] uppercase tracking-[0.06em] ' +
              (d.active ? 'text-accent-text' : 'text-ink-3')
            }
          >
            {d.dow}
          </span>
          <span
            className={
              // Muted (weekend) days dim via --ink-3 — still AA — instead of
              // an opacity wash, which halved the label contrast below 4.5:1.
              'mt-[3px] block text-[17px] font-semibold tracking-[-0.02em] ' +
              (d.muted ? 'text-ink-3' : 'text-ink')
            }
          >
            {d.dayNum}
          </span>
          <span
            aria-hidden
            className={
              'mx-auto mt-1 block size-1 rounded-full ' +
              (d.pip ? (d.muted ? 'bg-ok/50' : 'bg-ok') : 'bg-transparent')
            }
          />
        </button>
      ))}
    </div>
  );
}

/* ── Utilization card ──────────────────────────────────────────────────── */

interface UtilCardProps {
  /** Day headline (e.g. <>Úterý <em>2. čvn.</em></>). */
  title: ReactNode;
  /** Capacity utilization 0–100. */
  pct: number;
  stats: { label: string; value: ReactNode }[];
}

function UtilCard({ title, pct, stats }: UtilCardProps) {
  const clamped = Math.min(100, Math.max(0, pct));
  return (
    <div data-testid="agenda-util" className="border border-line bg-paper px-4 py-3.5">
      <div className="flex items-baseline justify-between">
        <div className="text-[16px] font-bold tracking-[-0.02em] text-ink [&_em]:font-normal [&_em]:italic [&_em]:text-ink-3">
          {title}
        </div>
        <div className="font-mono text-[13px] text-ink-2">{pct} % kapacity</div>
      </div>
      <div
        className="relative mt-2.5 h-[5px] bg-bg"
        role="progressbar"
        aria-label="Vytížení dne"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="absolute inset-y-0 left-0 bg-accent"
          style={{ width: `${clamped}%` }}
        />
      </div>
      <div
        className="mt-3 grid gap-px border border-line bg-line"
        style={{ gridTemplateColumns: `repeat(${Math.max(1, stats.length)}, 1fr)` }}
      >
        {stats.map((s) => (
          <div key={s.label} className="bg-paper px-2.5 py-2">
            <div className="font-mono text-[8.5px] uppercase tracking-[0.06em] text-ink-3">
              {s.label}
            </div>
            <div className="mt-0.5 text-[16px] font-bold tracking-[-0.02em] text-ink">
              {s.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Hour divider + job card ───────────────────────────────────────────── */

function Hour({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-2 pb-2 pt-4 font-mono text-[10px] uppercase tracking-[0.1em] text-ink-3 after:h-px after:flex-1 after:bg-line after:content-['']">
      {children}
    </div>
  );
}

interface JobProps {
  /** Mono start time on the headline ("07:00"). Omit for date-only items. */
  time?: string;
  /** Bold headline (customer / event title). */
  title: ReactNode;
  /** "Nyní" badge — the job is in progress right now. */
  now?: boolean;
  /** Second line (the task / description). */
  task?: ReactNode;
  /** Footer meta, left side (e.g. technician with a colored dot). */
  meta?: ReactNode;
  /** Footer meta, right-aligned (e.g. license plate). */
  metaRight?: ReactNode;
  tone?: EventChipTone;
  /** Confirmation state (servis): a small label after the title. */
  status?: EventChipStatus | null;
  onClick?: () => void;
  className?: string;
}

const STATUS_LABEL: Record<EventChipStatus, string> = {
  probehlo: 'Proběhlo',
  neprobehlo: 'Neproběhlo',
  k_potvrzeni: 'K potvrzení',
};

function Job({
  time,
  title,
  now = false,
  task,
  meta,
  metaRight,
  tone = 'neutral',
  status = null,
  onClick,
  className = '',
}: JobProps) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={
        'mb-2 block w-full border border-line border-l-[3px] bg-paper py-3 pl-4 pr-3.5 text-left ' +
        STRIPE_CLASSES[tone] +
        (onClick
          ? ' cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent'
          : '') +
        ' ' +
        className
      }
    >
      <span className="flex items-baseline gap-2.5">
        {time && (
          <span className="shrink-0 font-mono text-[12px] text-ink-2">{time}</span>
        )}
        <span className="min-w-0 flex-1 truncate text-[14.5px] font-semibold tracking-[-0.01em] text-ink">
          {title}
        </span>
        {status && (
          <span
            className={
              'flex shrink-0 items-center gap-1 font-mono text-[9.5px] uppercase tracking-[0.06em] ' +
              (status === 'probehlo'
                ? 'text-ok'
                : status === 'neprobehlo'
                  ? 'text-red-text'
                  : 'text-warn')
            }
          >
            <Icon
              name={status === 'probehlo' ? 'check' : status === 'neprobehlo' ? 'close' : 'clock'}
              size={11}
              stroke={2}
            />
            {STATUS_LABEL[status]}
          </span>
        )}
        {now && (
          <span className="shrink-0 bg-accent-fill px-1.5 py-0.5 font-mono text-[8.5px] uppercase tracking-[0.08em] text-accent-ink">
            Nyní
          </span>
        )}
      </span>
      {task && (
        <span className="mt-[5px] block text-[12.5px] leading-[1.4] text-ink-2">
          {task}
        </span>
      )}
      {(meta || metaRight) && (
        <span className="mt-2 flex items-center gap-2.5 font-mono text-[11px] text-ink-3">
          {meta}
          {metaRight && <span className="ml-auto">{metaRight}</span>}
        </span>
      )}
    </Tag>
  );
}

AgendaView.DayStrip = DayStrip;
AgendaView.UtilCard = UtilCard;
AgendaView.Hour = Hour;
AgendaView.Job = Job;
